import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiShield,FiEye,FiDatabase,FiUserCheck,FiLock,FiInfo,FiAlertTriangle} = FiIcons;

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: FiInfo,
      title: 'Information We Collect',
      content: [
        'Personal Information: Name, email address, phone number, and WhatsApp number when you contact us or use our services.',
        'Academic Information: Subject preferences, curriculum details, and learning objectives.',
        'Usage Data: Pages visited, time spent on our platform, and interaction patterns.',
        'Communication Records: Messages sent through our platform, WhatsApp conversations, and email communications.'
      ]
    },
    {
      icon: FiEye,
      title: 'How We Use Your Information',
      content: [
        'To provide and improve our tutoring and assignment support services.',
        'To match you with appropriate tutors based on your academic needs.',
        'To communicate with you regarding appointments, payments, and service updates.',
        'To ensure platform security and prevent fraudulent activities.',
        'To analyze usage patterns to enhance user experience and service quality.'
      ]
    },
    {
      icon: FiDatabase,
      title: 'Data Storage and Security',
      content: [
        'All personal data is stored securely using industry-standard encryption protocols.',
        'We implement appropriate technical and organizational measures to protect your data.',
        'Access to personal information is restricted to authorized personnel only.',
        'We regularly review and update our security measures to ensure data protection.'
      ]
    },
    {
      icon: FiUserCheck,
      title: 'Sharing Your Information',
      content: [
        'We do not sell, rent, or trade your personal information with third parties.',
        'Your information may be shared with assigned tutors only for service delivery purposes.',
        'We may disclose information if required by law or to protect our legal rights.',
        'Academic work details are shared only with relevant tutors and support staff.'
      ]
    },
    {
      icon: FiLock,
      title: 'Your Rights and Choices',
      content: [
        'Access: Request a copy of your personal information we hold.',
        'Correction: Update or correct inaccurate personal information.',
        'Deletion: Request removal of your personal data (subject to legal obligations).',
        'Opt-out: Unsubscribe from marketing communications at any time.',
        'Complaint: Raise concerns about data handling with regulatory authorities.'
      ]
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">Privacy Policy</h1>
          <h2 className="text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto">
            Your privacy is important to us. Learn how we collect, use, and protect your personal information.
          </h2>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-gray-600 text-center">
            <strong>Last Updated:</strong> January 2025 | <strong>Effective Date:</strong> January 1, 2025
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Tutor Hub Online is committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, store, and protect your data when you use our tutoring and 
              assignment support services.
            </p>
            <p className="text-gray-600 leading-relaxed">
              By using Tutor Hub Online, you acknowledge that you have read, understood, and agreed to the collection and 
              use of your information as outlined in this policy.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <SafeIcon icon={section.icon} className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {index + 1}. {section.title}
                    </h3>
                    <ul className="space-y-2">
                      {section.content.map((item, itemIndex) => (
                        <li key={itemIndex} className="text-gray-600 flex items-start">
                          <span className="text-primary mr-2 mt-2">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cookies Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-sm">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Cookies and Tracking</h3>
                <p className="text-gray-600 mb-4">
                  We use cookies and similar tracking technologies to enhance your experience on our platform:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>• <strong>Essential Cookies:</strong> Required for basic website functionality</li>
                  <li>• <strong>Performance Cookies:</strong> Help us understand how you use our platform</li>
                  <li>• <strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                  <li>• <strong>Marketing Cookies:</strong> Used to show relevant content and advertisements</li>
                </ul>
                <p className="text-gray-600 mt-4">
                  You can control cookie settings through your browser preferences, but disabling certain cookies 
                  may affect website functionality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Retention */}
      <section className="py-16 bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-sm">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <SafeIcon icon={FiDatabase} className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Data Retention</h3>
                <p className="text-gray-600 mb-4">
                  We retain your personal information only as long as necessary for the purposes outlined in this policy:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>• <strong>Active Users:</strong> Information retained throughout your service period</li>
                  <li>• <strong>Academic Records:</strong> Kept for 7 years after service completion for quality assurance</li>
                  <li>• <strong>Financial Records:</strong> Retained for 6 years as required by law</li>
                  <li>• <strong>Marketing Data:</strong> Kept until you unsubscribe or request deletion</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8">
            <div className="flex items-start space-x-4">
              <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-semibold text-yellow-800 mb-3">Important Notice</h3>
                <p className="text-yellow-700 mb-4">
                  By continuing to use Tutor Hub Online, you acknowledge that you have read, understood, and agreed 
                  to this Privacy Policy. We reserve the right to update this policy periodically to reflect changes 
                  in our practices or applicable laws.
                </p>
                <p className="text-yellow-700">
                  Any significant changes to this policy will be communicated to you via email or through prominent 
                  notices on our platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-sm text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Questions About Your Privacy?</h3>
            <p className="text-gray-600 mb-6">
              If you have any questions or concerns about this Privacy Policy or how we handle your data, 
              our privacy team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                Contact Privacy Team
              </Link>
              <a href="mailto:info@tutorhubonline.com" className="border-2 border-primary text-primary px-8 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors">
                Email Privacy Team
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Your Privacy Matters to Us</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            We are committed to maintaining the trust and confidence of our students and tutors through transparent 
            privacy practices and robust data protection measures.
          </p>
          <Link to="/book-tutor" className="bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-colors text-lg">
            Start Your Learning Journey
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;